import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, Database } from "lucide-react";

const STATUS = {
  synced: { label: "Synced", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  error: { label: "Error", cls: "bg-red-50 text-red-700 border-red-200" },
  not_synced: { label: "Never synced", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "Never");

function Row({ r }) {
  const s = STATUS[r.sync_status] || { label: r.sync_status, cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return (
    <tr className="align-top" data-testid={`sync-row-${r.id}`}>
      <td className="px-5 py-3 font-medium text-navy whitespace-nowrap">{r.dealership_name}</td>
      <td className="px-5 py-3">{r.name}<div className="text-xs text-muted-foreground">{r.connector_type}{r.auto_sync ? "" : " · auto sync off"}</div></td>
      <td className="px-5 py-3 whitespace-nowrap" data-testid={`sync-last-${r.id}`}>{fmt(r.last_synced_at)}</td>
      <td className="px-5 py-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${s.cls}`} data-testid={`sync-status-${r.id}`}>{s.label}</span></td>
      <td className="px-5 py-3">{r.last_record_count ?? "-"}</td>
      <td className="px-5 py-3 text-xs text-red-700 max-w-xs break-words" data-testid={`sync-error-${r.id}`}>{r.sync_error || ""}</td>
    </tr>
  );
}

export default function AdminInventorySync() {
  const [rows, setRows] = useState(null);
  const load = () => { setRows(null); api.get("/admin/inventory-sync").then(({ data }) => setRows(data)).catch(() => setRows([])); };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6 fade-up" data-testid="admin-inventory-sync">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-navy">Inventory Sync</h1>
          <p className="text-muted-foreground mt-1">Last sync time, status and error for every dealership's inventory feed.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="sync-refresh-btn"><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh</Button>
      </div>
      <div className="bg-white rounded-2xl border border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Database className="w-4 h-4 text-gold" />
          <h2 className="font-semibold text-navy">Inventory feeds</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-navy">
              <tr className="text-left">
                {["Dealership", "Feed", "Last sync", "Status", "Boats in last sync", "Error"].map((h) => <th key={h} className="px-5 py-3 font-semibold whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-border" data-testid="sync-table-body">
              {rows === null ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground" data-testid="sync-empty">No dealership has connected an inventory feed yet.</td></tr>
              ) : rows.map((r) => <Row key={r.id} r={r} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
